/**
 * WorkspaceDataPreloader, ActivityDataPreloader, AccountsDataPreloader & SettingsDataPreloader
 *
 * Invisible components that run Analytics/Activity/Accounts/Settings data hooks in the background
 * when the user is on another workspace page. When they later open that page,
 * the caches are already filled so it loads instantly.
 */

import { ReactElement } from "react";
import { useAnalyticsTransactions } from "../pages/Analytics/hooks/useAnalyticsTransactions";
import { useAnalyticsAccounts } from "../pages/Analytics/hooks/useAnalyticsAccounts";
import { useAnalyticsWorkspace } from "../pages/Analytics/hooks/useAnalyticsWorkspace";
import { useAnalyticsCategories } from "../pages/Analytics/hooks/useAnalyticsCategories";
import { useAnalyticsCurrencies } from "../pages/Analytics/hooks/useAnalyticsCurrencies";
import { useAnalyticsWorkspaceMembers } from "../pages/Analytics/hooks/useAnalyticsWorkspaceMembers";
import { useWorkspaceData } from "../pages/Activity/hooks/useWorkspaceData";
import { useTransactions } from "../pages/Activity/hooks/useTransactions";
import { useTransactionHistoryCheck } from "../pages/Activity/hooks/useTransactionHistoryCheck";
import { useAccounts } from "../pages/Activity/hooks/useAccounts";
import { useCurrencies } from "../pages/Activity/hooks/useCurrencies";
import { useCategories } from "../pages/Activity/hooks/useCategories";
import { useAccounts as useAccountsForAccounts } from "../pages/Accounts/hooks/useAccounts";
import { useAccountGroups } from "../pages/Accounts/hooks/useAccountGroups";
import { useCurrencies as useCurrenciesForAccounts } from "../pages/Accounts/hooks/useCurrencies";
import { useDefaultCurrency } from "../pages/CurrencySettings/hooks/useDefaultCurrency";
import { useCurrencies as useCurrenciesForSettings } from "../pages/CurrencySettings/hooks/useCurrencies";
import { useCategories as useCategoriesForSettings } from "../pages/CategorySettings/hooks/useCategories";
import { useCategoryGroups as useCategoryGroupsForSettings } from "../pages/CategorySettings/hooks/useCategoryGroups";
import { useTransactionCustomFieldsSettings } from "../pages/TransactionFieldsSettings/hooks/useTransactionCustomFieldsSettings";
import { useTransactionFieldUsage } from "../pages/TransactionFieldsSettings/hooks/useTransactionFieldUsage";
import { useWorkspace } from "../pages/WorkspaceSettings/hooks/useWorkspace";
import { useWorkspaceMembers } from "../pages/WorkspaceSettings/hooks/useWorkspaceMembers";
import { useWorkspaceInvitations } from "../pages/WorkspaceSettings/hooks/useWorkspaceInvitations";
import { useWorkspaceSettings } from "../pages/WorkspaceSettings/hooks/useWorkspaceSettings";

export interface WorkspaceDataPreloaderProps {
  workspaceId: string;
}

/**
 * Preloads Analytics data. Mount when NOT on the Analytics page.
 */
export function WorkspaceDataPreloader({ workspaceId }: WorkspaceDataPreloaderProps): ReactElement | null {
  const { defaultCurrency } = useAnalyticsWorkspace({ workspaceId });
  useAnalyticsTransactions({ workspaceId });
  useAnalyticsCategories({ workspaceId });
  useAnalyticsAccounts({ workspaceId });
  useAnalyticsCurrencies({ workspaceId, defaultCurrency: defaultCurrency || "USD" });
  useAnalyticsWorkspaceMembers({ workspaceId });
  return null;
}

/**
 * Preloads Activity data (transactions, workspace, accounts, currencies, categories, history check).
 * Mount when NOT on the Activity page so Activity opens instantly with edited icons already known.
 */
export function ActivityDataPreloader({ workspaceId }: WorkspaceDataPreloaderProps): ReactElement | null {
  const workspaceData = useWorkspaceData({ workspaceId });
  const { transactions } = useTransactions({ workspaceId, membershipEnsured: workspaceData.membershipEnsured });
  useTransactionHistoryCheck(workspaceId, transactions);
  useAccounts({ workspaceId });
  useCurrencies({ workspaceId });
  useCategories({ workspaceId });
  return null;
}

/**
 * Preloads Accounts data (accounts, account groups, currencies).
 * Mount when NOT on the Accounts page so Accounts opens instantly after refresh or navigation.
 */
export function AccountsDataPreloader({ workspaceId }: WorkspaceDataPreloaderProps): ReactElement | null {
  useAccountsForAccounts(workspaceId);
  useAccountGroups(workspaceId);
  useCurrenciesForAccounts(workspaceId);
  return null;
}

/**
 * Preloads Settings data (workspace, currencies, categories, groups, transaction fields).
 * Mount when NOT on the Settings page so Settings sections and Manage Details open instantly.
 */
export function SettingsDataPreloader({ workspaceId }: WorkspaceDataPreloaderProps): ReactElement | null {
  const { defaultCurrency } = useDefaultCurrency({ workspaceId });
  const { workspace } = useWorkspace({ workspaceId });
  useCurrenciesForSettings({ workspaceId, defaultCurrency: defaultCurrency ?? "USD" });
  useCategoriesForSettings({ workspaceId, type: "expense" });
  useCategoriesForSettings({ workspaceId, type: "income" });
  useCategoryGroupsForSettings({ workspaceId, type: "expense" });
  useCategoryGroupsForSettings({ workspaceId, type: "income" });
  useTransactionCustomFieldsSettings({ workspaceId });
  useTransactionFieldUsage({ workspaceId });
  useWorkspaceMembers({ workspaceId, workspace });
  useWorkspaceInvitations({ workspaceId });
  useWorkspaceSettings({ workspaceId });
  return null;
}
